import { KeyboardEvent } from 'react';
import { htmlToJson, jsonToHtml } from "@contentstack/json-rte-serializer";
import { Field, IAudience, IExperience, IExperienceVersion } from "../types";
import { AppRegion, KeyValueObj, RegionEndpoints } from "../common/types/types";
import { uuid } from 'uuidv4';
import Extension from "@contentstack/app-sdk/dist/src/uiLocation";
import { get, isEmpty, keys } from "lodash";
import axios from 'axios';
import { STORAGE_KEYS, SUPPORTED_IMAGE_GEN_MODELS, TEXT_PROVIDER } from '../constant';
import { getRegionEndpoint } from '../common/helper';

export const buildEntryData = (entry: any) => {
  const remodeledEntryData: KeyValueObj = {};

  entry?.content_type?.schema.forEach((field: Field) => {
    if (
      field.data_type === "text" ||
      field.data_type === "blocks" ||
      field.data_type === "group" ||
      field.data_type === "global_field" ||
      field.data_type === "json"
    ) {
      remodeledEntryData[field["uid"]] = field;
    }
  });

  remodeledEntryData["tags"] = entry?.getData()?.tags;

  return remodeledEntryData;
};

export const getGroupTitleUID = function (field: Field, instanceIndex: number) {
  var grouptitle = null,
    isTitleFlag,
    isDefaultFlag;
  if (field.schema) {
    for (var i = 0, total = field.schema.length; i < total; i++) {
      if (field.schema[i].field_metadata && field.schema[i].field_metadata.isTitle) {
        isTitleFlag = i;
        break;
      } else if (
        (field.schema[i].uid === "title" || field.schema[i].uid === "cs_group_title_" + field.uid) &&
        field.schema[i].field_metadata &&
        field.schema[i].field_metadata._default
      ) {
        isDefaultFlag = i;
      }
    }
  }
  grouptitle =
    typeof isTitleFlag == "number"
      ? field.value[instanceIndex][field.schema[isTitleFlag].uid].value || field.schema[isTitleFlag].uid
      : typeof isDefaultFlag == "number"
        ? field.schema[isDefaultFlag].uid
        : null;
  return grouptitle;
};

export const getTopLevelIcons = (field: Field) => {
  let icons: KeyValueObj = {
    title: "StarSmall",
    text: "SingleLineTextSmall",
    multitext: "MultiLineTextSmall",
    rte: "RichTextEditorSmall",
    jsonRte: "SuperchargedRte",
    markdown: "MarkdownSmall",
    select: "SelectSmall",
    number: "NumberSmall",
    boolean: "BooleanSmall",
    isodate: "DateSmall",
    file: "FileSmall",
    reference: "ReferenceSmall",
    group: "GroupSmall",
    global_field: "GlobalSmall",
    blocks: "ModularBlocksSmall",
    link: "LinkSmall",
    bullet: "Bullet",
    custom: "CustomSmall",
    tag: "TagSmall",
    experience_container: "PersonalizationLogoGreySmall",
  };

  if (field.uid === "title") {
    return icons["title"];
  }

  if (field.uid === "tags") {
    return icons["tag"];
  }

  if (field?.data_type === "json" && field.field_metadata?.allow_json_rte) {
    return icons["jsonRte"];
  }

  if (field.data_type === "text") {
    if (field.uid === "url" && field.$uid === "url") {
      return icons["link"];
    }
    if (field && field.field_metadata && field.field_metadata.multiline) {
      return icons["multitext"];
    }
    if (field && field.field_metadata && field.field_metadata.allow_rich_text) {
      return icons["rte"];
    }
    if (field && field.field_metadata && field.field_metadata.markdown) {
      return icons["markdown"];
    }
    if (field && field.enum) {
      return icons["select"];
    }
  }
  if (field && field.field_metadata && field.field_metadata.extension) {
    return icons["custom"];
  }

  if (!field.data_type) {
    return icons["blocks"];
  }

  return icons[field.data_type];
};

// block as title
export const abstractTitleUID = (block: KeyValueObj) => {
  for (var key in block) {
    var blockVal = block[key].value;
    for (var blockKey in blockVal) {
      var titleValue = blockVal[blockKey];
      if (titleValue.data_type === "link") {
        return titleValue.uid;
      } else if (
        titleValue.data_type === "json" &&
        titleValue.field_metadata &&
        !titleValue.field_metadata.allow_json_rte &&
        !titleValue.field_metadata.extension
      ) {
        return titleValue.uid;
      } else if (
        titleValue.data_type === "text" &&
        titleValue.field_metadata &&
        !titleValue.field_metadata.allow_rich_text &&
        !titleValue.field_metadata.markdown
      ) {
        return titleValue.uid;
      } else if (titleValue.data_type === "file") {
        return titleValue.uid;
      } else if (titleValue.data_type === "number") {
        if (titleValue.multiple === true) {
          return titleValue.uid;
        } else {
          return titleValue.uid;
        }
      } else if (titleValue.data_type === "isodate") {
        return titleValue.uid;
      }
    }
  }
};

export const getGroupTitleBlankValue = function (value: any) {
  if (value === null) {
    return true;
  }
  if (!value && value && !value.filename) {
    return true;
  } else if (value && (value.date === "" || value.time === "") && !(value.date.length || value.time.length)) {
    return true;
  } else if (value && (value.title === "" || value.href === "") && !(value.title.length || value.href.length)) {
    return true;
  } else {
    return false;
  }
};

export const getBlockTitle = (block: KeyValueObj, blockUid: string) => {
  var blockTitle = "";
  const fieldUid = abstractTitleUID(block);
  if (block[blockUid] && block[blockUid].value[fieldUid]) {
    if (block[blockUid].value[fieldUid].multiple) {
      if (
        block[blockUid].value[fieldUid].value[0] &&
        block[blockUid].value[fieldUid].value[0].file &&
        block[blockUid].value[fieldUid].value[0].file.filename &&
        block[blockUid].value[fieldUid].value[0].file.filename.length
      ) {
        blockTitle = block[blockUid].value[fieldUid].value[0].file.filename;
      } else if (
        block[blockUid].value[fieldUid].value[0] &&
        (block[blockUid].value[fieldUid].value[0].length ||
          (block[blockUid].value[fieldUid].value[0].value && block[blockUid].value[fieldUid].value[0].value.length))
      ) {
        blockTitle =
          (block[blockUid].value[fieldUid] && block[blockUid].value[fieldUid].value[0].value) ||
          (block[blockUid].value[fieldUid][0] && block[blockUid].value[fieldUid][0].value[0]);
      } else if (block[blockUid].value[fieldUid].data_type === "number" && block[blockUid].value[fieldUid].value[0]) {
        blockTitle = block[blockUid].value[fieldUid].value[0].key;
      } else if (
        block[blockUid].value[fieldUid].value &&
        block[blockUid].value[fieldUid].value[0] &&
        (block[blockUid].value[fieldUid].value[0].href || block[blockUid].value[fieldUid].value[0].title)
      ) {
        if (block[blockUid].value[fieldUid].value[0].title && !block[blockUid].value[fieldUid].value[0].filename) {
          blockTitle = block[blockUid].value[fieldUid].value[0].title;
        } else if (block[blockUid].value[fieldUid].value[0].href) {
          blockTitle = block[blockUid].value[fieldUid].value[0].href;
        }
      } else if (
        block[blockUid].value[fieldUid].data_type !== "number" &&
        block[blockUid].value[fieldUid].value[0] &&
        block[blockUid].value[fieldUid].value[0].key
      ) {
        blockTitle = block[blockUid].value[fieldUid].value[0].key;
      } else if (
        block[blockUid].value[fieldUid].value[0] &&
        block[blockUid].value[fieldUid].value[0].date &&
        block[blockUid].value[fieldUid].value[0].date.length
      ) {
        blockTitle = "getGroupTitleValue";
      } else if (getGroupTitleBlankValue(block[blockUid].value[fieldUid].value[0])) {
        blockTitle = "Untitled";
      }
    } else {
      if (
        block[blockUid] &&
        block[blockUid].value[fieldUid].value &&
        block[blockUid].value[fieldUid].value.filename &&
        block[blockUid].value[fieldUid].value.filename.length
      ) {
        blockTitle = block[blockUid].value[fieldUid].value.filename;
      } else if (
        block[blockUid].value[fieldUid].value &&
        (block[blockUid].value[fieldUid].value.length ||
          (block[blockUid].value[fieldUid].value.value && block[blockUid].value[fieldUid].value.value.length))
      ) {
        blockTitle = block[blockUid].value[fieldUid].value.value || block[blockUid].value[fieldUid].value;
      } else if (block[blockUid].value[fieldUid].data_type === "number" && block[blockUid].value[fieldUid].value) {
        blockTitle = block[blockUid].value[fieldUid].value;
      } else if (
        block[blockUid].value[fieldUid].value &&
        (block[blockUid].value[fieldUid].value.href || block[blockUid].value[fieldUid].value.title)
      ) {
        if (block[blockUid].value[fieldUid].value.title && !block[blockUid].value[fieldUid].value.filename) {
          blockTitle = block[blockUid].value[fieldUid].value.title;
        } else if (block[blockUid].value[fieldUid].value.href) {
          blockTitle = block[blockUid].value[fieldUid].value.href;
        }
      } else if (
        block[blockUid].value[fieldUid].value &&
        block[blockUid].value[fieldUid].value.date &&
        block[blockUid].value[fieldUid].value.date.length
      ) {
        blockTitle = "getGroupTitleValue";
      } else if (getGroupTitleBlankValue(block[blockUid].value[fieldUid].value)) {
        blockTitle = "Untitled";
      }
    }
  }
  blockTitle = blockTitle && typeof blockTitle == "string" ? " (" + blockTitle + ")" : "";
  return blockTitle;
};

export const shouldFieldBeDisabled = (field: KeyValueObj) => {
  const isJsonRte = field?.data_type === "json" && field?.field_metadata?.allow_json_rte;
  const hasEmptyContent =
    typeof field?.value === "string" && (field?.value === "" || field?.value?.replace(/ /g, "") === "<p></p>");
  let isJsonRteEmpty = false;
  if (isJsonRte) {
    isJsonRteEmpty = jsonToHtml(field.value)?.replace(/ /g, "") === `<pdirty="true"></p>`;
  }
  return hasEmptyContent || isJsonRteEmpty;
};

function validateHTML(htmlString: string) {
  if (htmlString.endsWith("<") || htmlString.endsWith("</")) {
    return false;
  }
  return true;
}

const removeBeginningNewLineInPara = (json: any) => {
  const modifiedChildren: any = [];

  for (let i = 0; i < json.children.length; i++) {
    const currentChild = json.children[i];
    modifiedChildren.push(currentChild);

    if (currentChild.type === "p" && i + 1 < json.children.length && json.children[i + 1].type === "fragment") {
      const nextFragment = json.children[i + 1];
      nextFragment.children[0].text = nextFragment.children[0].text.trim();
    }
  }

  json.children = modifiedChildren;

}

export const transformToJsonRTEFormat = (inputText: string) => {
  if (!validateHTML(inputText)) return null;
  const htmlDom = new DOMParser().parseFromString(inputText, "text/html");
  const htmlDomBody = htmlDom.body;
  const jsonFormatedValue: any = htmlToJson(htmlDomBody);
  removeBeginningNewLineInPara(jsonFormatedValue)
  return jsonFormatedValue;
};

export const hasTextContent = (htmlResponse: string) => {
  let tempEle = document.createElement("div");
  tempEle.innerHTML = htmlResponse;
  // Check if the temp element has non-empty text nodes
  var hasText = Array.from(tempEle.childNodes).some(node => {
    return (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) && node.textContent?.trim() !== "";
  });
  return hasText
}
export const isMasked = (key: string) =>
  typeof key === "string" && /^\*{4,}[^*]{4,}$/.test(key);

export const areKeysMasked = (config: any, provider: string): boolean => {
  if (provider === TEXT_PROVIDER.VERTEX_AI) {
    try {
      const parsedKey = JSON.parse(config.access_key || '{}');
      return (
        isMasked(parsedKey.private_key_id) &&
        isMasked(parsedKey.private_key) &&
        isMasked(parsedKey.client_email)
      );
    } catch {
      return false; // fallback: keys are not masked if parsing fails
    }
  }

  return (
    isMasked(config.api_key) ||
    (isMasked(config.access_key) && isMasked(config.secret_access_key))
  );
};

export function startsWithSpace(input: string): boolean {
  // Check if the string starts with a space
  const regex = /^\s/;
  return regex.test(input);
}

export function endsWithSpace(input: string): boolean {
  // Check if the string ends with a space
  const regex = /\s$/;
  return regex.test(input);
}

export function startsWithCapital(sentence: string): boolean {
  // Check if the first character is a capital letter
  const pattern = /^[A-Z]/;
  return pattern.test(sentence);
}

//function to find the first text node
export function findFirstTextNode(node: Node): Node | null {
  if (!node) return null;
  if (node.nodeType === Node.TEXT_NODE && node?.nodeValue?.trim() !== '') {
    return node;
  }

  for (const childNode of Array.from(node?.childNodes)) {
    const firstText: Node | null = findFirstTextNode(childNode);
    if (firstText) {
      return firstText;
    }
  }

  return null;
}

// function to find the last text node
function findLastTextNode(node: Node): Node | null {
  if (node.nodeType === Node.TEXT_NODE && node?.nodeValue?.trim() !== '') {
    return node;
  }

  const childNodes = Array.from(node.childNodes).reverse();
  for (const childNode of childNodes) {
    const lastText = findLastTextNode(childNode);
    if (lastText) {
      return lastText;
    }
  }

  return null;
}

export function capitalizeOrLowercaseFirstTextNode(htmlString: string, capitalize: boolean) {
  // Create a temporary DOM element
  const tempElement = document.createElement('div');
  tempElement.innerHTML = htmlString;

  const firstTextNode = findFirstTextNode(tempElement);

  if (firstTextNode) {
    // Update the first letter of the text node
    const text = firstTextNode?.nodeValue;
    let updatedText = '';
    if (capitalize) {
      updatedText = text?.trim().charAt(0).toUpperCase() || ''
    } else {
      updatedText = text?.trim().charAt(0).toLowerCase() || ''
    }
    firstTextNode.nodeValue = updatedText + text?.slice(1);
  }

  // Get the updated HTML string
  const updatedHtmlString = tempElement.innerHTML;

  return updatedHtmlString;
}

export function getLastSentencePunctuation(text: string) {
  // Find the last punctuation mark in the text
  const punctuationMatch = text.trim().match(/[.,;:?!\-\/_|\~]+$/);

  if (punctuationMatch) {
    return punctuationMatch[0];
  }

  return '';
}

export function replaceLastTextNodePunctuation(htmlString: string, newPunctuation = '') {

  const tempElement = document.createElement('div');
  tempElement.innerHTML = htmlString;
  const lastTextNode = findLastTextNode(tempElement);
  const punctuationRegExp = /[.,;:?!\-/|~]+$/;
  if (lastTextNode) {
    // Get the text content of the last text node
    let text = lastTextNode?.nodeValue;
    if (!text?.trim()) return;
    // get the last sentence's punctuation
    const punctuationMatch = text?.trim()?.match(punctuationRegExp);

    if (punctuationMatch) {
      // Replace the last punctuation with the new punctuation or remove it
      text = text.replace(punctuationRegExp, newPunctuation);
      lastTextNode.nodeValue = text;
    } else if (newPunctuation) {
      text = text + newPunctuation
      lastTextNode.nodeValue = text;
    }
  }

  // Get the updated HTML string
  const updatedHtmlString = tempElement.innerHTML;

  return updatedHtmlString;
}

export const generateUid = (prefix?: string) => {
  prefix = prefix ? `${prefix}-` : '';
  return `${prefix}${uuid()}`;
}


export const base64toBlob = (base64: string, contentType = '', sliceSize = 512) => {
  if (!base64) return new Blob([], { type: contentType });
  const byteCharacters = atob(base64);
  const byteArrays: Uint8Array[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

export const base64toURL = (base64: string, contentType = 'image/png') => {
  const blob = base64toBlob(base64, contentType)
  return URL.createObjectURL(blob);
}

export const base64toFile = (base64: string, fileName = '', fileType = 'image/png'): File => {
  const blob = base64toBlob(base64, fileType);
  return new File([blob], fileName, { type: fileType });
}

export const urlToBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await axios.get(url, { responseType: 'arraybuffer' });
    const buffer = Buffer.from(response.data, 'binary');
    return buffer.toString('base64');
  } catch (error) {
    console.error('Error fetching image:', error);
    return null;
  }
};

export const onEnterKeyPress = (event: KeyboardEvent<HTMLInputElement>, callback: Function) => {
  if (event?.keyCode === 13) {
    event.preventDefault()
    callback();
  }
}

export const insertAtIndex = <T>(index: number, arr: Array<T>, element: T,) => {
  arr.splice(index, 0, element)
}

export function getAppLocation(sdk: Extension): string {
  const locations = keys(sdk?.location);
  let locationName = "";
  for (let i = 0; i <= locations.length; i++) {
    if (!isEmpty(get(sdk, `location.${locations[i]}`, undefined))) {
      locationName = locations[i];
      break;
    }
  }
  return locationName;
}

export function setDataInLocalStorage(key: string, value: any) {
  try {
    if (typeof localStorage !== "undefined") {
      // Safe to use localStorage
      localStorage.setItem(key, value);
    } else {
      // localStorage is not supported
      console.error("localStorage is not supported");
    }
  } catch (e) {
    // Handle the exception
    console.log("Something went wrong in setting data in local storage");
  }
}

export function getDataFromLocalStorage(key: string) {
  try {
    if (typeof localStorage !== "undefined") {
      // Safe to use localStorage
      return localStorage.getItem(key);
    } else {
      // localStorage is not supported
      console.error("localStorage is not supported");
    }
  } catch (e) {
    // Handle the exception
    console.log("Something went wrong in getting data from local storage");
  }
  return null;
}

export function removeDataFromLocalStorage(key: string) {
  try {
    if (typeof localStorage !== "undefined") {
      // Safe to use localStorage
      return localStorage.removeItem(key);
    } else {
      // localStorage is not supported
      console.error("localStorage is not supported");
    }
  } catch (e) {
    // Handle the exception
    console.log("Something went wrong while removing data from local storage");
  }
}

export const makeNetworkRequestWithAuthToken = (
  url: string,
  config: Record<string, any>,
  callback: (event: any, resolve: Function, reject: Function, cancelRequest: Function) => void
) => {
  return new Promise((resolve, reject) => {
    const uniqueId = generateUid()
    window.postRobot.sendToParent('makeNetworkRequestForAiAssitant', { uid: uniqueId });

    const cancelRequest = () => {
      window.parent.postMessage(
        {
          type: `cancelRequest_${uniqueId}`,
        },
        "*"
      );
    }

    window.postRobot.on(`provideRequestConfig_${uniqueId}`, () => {
      window.parent.postMessage(
        {
          type: `sendRequestConfig_${uniqueId}`,
          url,
          config
        },
        "*"
      );

      window.postRobot.on(`networkResponse_${uniqueId}`, (event: any) => callback(event, resolve, reject, cancelRequest))
    })
  })
}

export const jsonRespCallback = async (event: any, resolve: Function, reject: Function) => {
  try {
    const responsePromise = event?.data?.response
    resolve(responsePromise);
  } catch (error) {
    console.error(error)
    reject(error)
  }
}

export const getExperience = async (region: AppRegion, expUid: string, projectUid: string, apiKey: string): Promise<IExperience> => {
  const url = `${getPersonalizeEndpoint(region)}/experiences/${expUid}`
  const config = {
    method: 'GET',
    headers: {
      "Content-Type": "application/json",
      "x-project-uid": projectUid,
      "api_key": apiKey,
    }
  }
  const response: string = await makeNetworkRequestWithAuthToken(url, config, jsonRespCallback) as string;
  const experience = await JSON.parse(response)
  return experience;
}
export const getLatestExperienceVersion = async (region: AppRegion, expUid: string, projectUid: string, apiKey: string): Promise<IExperienceVersion | undefined> => {
  const url = `${getPersonalizeEndpoint(region)}/experiences/${expUid}/versions`
  const config = {
    method: 'GET',
    headers: {
      "Content-Type": "application/json",
      "x-project-uid": projectUid,
      "api_key": apiKey,
    }
  }
  const response: string = await makeNetworkRequestWithAuthToken(url, config, jsonRespCallback) as string;
  try {
    const experienceVerions : IExperienceVersion[] = await JSON.parse(response)
    if (experienceVerions.length) {
      const activeVersion = experienceVerions.find((exp) => exp.status === 'ACTIVE');
      if (!activeVersion) {
        return experienceVerions[0]
      } else {
        return activeVersion;
      }
    }
  } catch(err) {
    console.error('Failed to parse experiences version', err)
  }
}

export const getAudienceList = async (region: AppRegion, audienceUids: string[], projectUid: string) => {
  if(!audienceUids.length) return []
  const url = `${getPersonalizeEndpoint(region)}/audiences`
  const config = {
    method: 'GET',
    headers: {
      "Content-Type": "application/json",
      "x-project-uid": projectUid
    }
  }
  const response: string = await makeNetworkRequestWithAuthToken(url, config, jsonRespCallback) as string;
  const audienceList: IAudience[] = await JSON.parse(response)
  const currentAudiences = audienceList.filter((audience) => audienceUids.includes(audience.uid))
  return currentAudiences
}

export const isPersonalizeEnabledOnCurrentPlan = async () => {
  const personalize = await window.postRobot.sendToParent('isPlanEnabled', {
    planKey: 'personalizationAccess'
  })
  return personalize?.data?.personalizationAccess;
}

export const getLocaleList = async (appRegion: AppRegion) => {
  let locales = getDataFromLocalStorage(STORAGE_KEYS.LOCALES)
  if (!locales) {
    const config = {
      method: 'get',
      url: `${getAppHostEndpoint(appRegion)}/api/v3/locales?include_all=true`,
    }
    try {
      const response = await axios.request(config);
      locales = response.data;
      setDataInLocalStorage(STORAGE_KEYS.LOCALES, JSON.stringify(locales))
      return locales;
    } catch (error) {
      console.log(error);
    }
  } else {
    return JSON.parse(locales);
  }
}

const cache_expiry = 60 * 60 * 1000;
export async function ensureDataInLocalStorage(key: string, cb: () => Promise<any>) {
  try {
    let res = getDataFromLocalStorage(key);
    let reFetch = false;
    let data;
    const now = new Date().getTime();
    if (res) {
      const obj = JSON.parse(res);
      reFetch = (now - obj.ts) > cache_expiry;
      data = obj.data;
    } else {
      reFetch = true;
    }
    if (reFetch) {
      data = await cb();
      if (!isEmpty(data)) {
        const str = JSON.stringify({ ts: now, data })
        setDataInLocalStorage(key, str)
      }
    }
    return data;
  } catch (e) {
    console.log("#ensureDataInLocalStorage", "Something went wrong in getting data from local storage");
  }
}
export const getCustomLocaleList = async (appRegion: AppRegion, apiKey: string) => {
  const url = `${getAppHostEndpoint(appRegion)}/api/v3/locales`
  const config = {
    method: 'GET',
    headers: {
      "Content-Type": "application/json",
      "api_key": apiKey,
    }
  }
  const response: string = await makeNetworkRequestWithAuthToken(url, config, jsonRespCallback) as string;
  const entryLocaleList = JSON.parse(response);
  const CustomLocaleList: { [key: string]: string } = {};
  entryLocaleList.locales.forEach((locale: { code: string; name: string }) => {
    CustomLocaleList[locale.code] = locale.name;
  });
  return CustomLocaleList;
}

export const decodeAndStripHtml = (inputText: string, allowedTags?: RegExp): string => {
  if (!validateHTML(inputText)) return inputText;

  // Parse and sanitize the input HTML
  const htmlDom = new DOMParser().parseFromString(inputText, "text/html");
  const bodyContent = allowedTags ? htmlDom.body.innerHTML : htmlDom.documentElement.textContent; // Preserve the full HTML content

  if (bodyContent) {
    // Replace all tags, but preserve allowed tags like <strong> and </strong>
    const strippedString = bodyContent.replace(
      allowedTags ? /<\/?([a-zA-Z0-9]+)[^>]*>/g : /<\/?[^>]+(>|$)/g,
      (match, tagName) => {
        // If the tag matches the allowed tags regex (i.e., <strong> or </strong>), keep it
        if (allowedTags && allowedTags.test(tagName.toLowerCase())) {
          return match; // Preserve the <strong> or </strong> tag
        }
        return ""; // Strip all other tags
      }
    );

    return strippedString;
  }
  return "";
};

export const NoneOptions = {
  label: 'None',
  value: 'None',
}

const brandkitEndpointsString = process.env.REACT_APP_BRAND_KIT_BASE_URLS;
let brandkitEndpoints: RegionEndpoints;
if (brandkitEndpointsString) {
  brandkitEndpoints = JSON.parse(brandkitEndpointsString);
}
export const getBrandkitEndpoint = (region?: AppRegion) => getRegionEndpoint(brandkitEndpoints, region);

const genAIEndpointsString = process.env.REACT_APP_GEN_AI_BASE_URLS;
let genAIEndpoints: RegionEndpoints;
if (genAIEndpointsString) {
  genAIEndpoints = JSON.parse(genAIEndpointsString);
}
const genAIPath = process.env.REACT_APP_GEN_AI_PATH;
export const getGenAIEndpoint = (region?: AppRegion) => getRegionEndpoint(genAIEndpoints, region) + genAIPath;

const appHostEndpointsString = process.env.REACT_APP_HOST_BASE_URLS;
let appHostEndpoints: RegionEndpoints;
if (appHostEndpointsString) {
  appHostEndpoints = JSON.parse(appHostEndpointsString);
}
export const getAppHostEndpoint = (region?: AppRegion) => getRegionEndpoint(appHostEndpoints, region);

const personalizeEndpointsString = process.env.REACT_APP_PERSONALIZE_BASE_URLS;
let personalizeEndpoints: RegionEndpoints;
if (personalizeEndpointsString) {
  personalizeEndpoints = JSON.parse(personalizeEndpointsString);
  console.log("personalizeEndpoints", JSON.stringify(personalizeEndpoints));
}
export const getPersonalizeEndpoint = (region?: AppRegion) => getRegionEndpoint(personalizeEndpoints, region);


const aiaAPIEndpointsString = process.env.REACT_APP_BACKEND_BASE_API_URLS;
let aiaAPIEndpoints: RegionEndpoints;
if (aiaAPIEndpointsString) {
  aiaAPIEndpoints = JSON.parse(aiaAPIEndpointsString);
  console.log("aiaAPIEndpoints", JSON.stringify(aiaAPIEndpoints));
}
export const getAIAAPIEndpoint = (region?: AppRegion) => getRegionEndpoint(aiaAPIEndpoints, region);

const enableBrainstromEnv = process.env.REACT_APP_ENABLE_BRAINSTORM;
export const enableBrainstrom = enableBrainstromEnv == 'true';

const reDirectGenAIEnv = process.env.REACT_APP_REDIRECT_GENAI;
export const reDirectGenAI = reDirectGenAIEnv == 'true';

// JSON RTE Prompt labels as per key
export const JSONRTEPromptLabels: KeyValueObj = {
  'Make shorter': 'Change length to shorter',
  'Make longer': 'Change length to longer',
}